<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('supplier.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('supplier.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">{{
            $t('add_new')
          }}</ts-button>
          <div class="d-flex">
            <ts-input
              v-model="search"
              v-tooltip="$t('supplier.search')"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
          </div>
        </div>
      </ts-card-wrapper>
      <Table
        :data="resources"
        :columns="columns"
        size="small"
        highlight-row
        stripe
        max-height="700"
        :loading="loading"
      >
        <template slot-scope="{ row }" slot="contactNumber">
          <p class="ts-mb-1 ts-mt-1">{{ row.contact_number1 }}</p>
          <p class="ts-mb-1">{{ row.contact_number2 }}</p>
        </template>
        <template slot-scope="{ row }" slot="isLocal">
          <span class="badge badge-success" v-if="row.is_local">Yes</span>
          <span class="badge badge-danger" v-else>No</span>
        </template>
        <template slot-scope="{ row }" slot="vendorIdTin">
          <p class="ts-mb-1">{{ row.vendor_id_tin }}</p>
        </template>
        <template slot-scope="{ row }" slot="vendorType">
          <p class="ts-mb-1">{{ vendorTypeLabel(row.vendor_type) }}</p>
        </template>
        <template slot-scope="{ row }" slot="action">
          <a
            href="#"
            v-tooltip="$t('edit')"
            class="text-primary mr-2"
            @click.prevent="edit(row)"
          >
            <i class="fas fa-edit"></i>
          </a>
          <a-popconfirm
            placement="leftTop"
            :title="$t('are_you_sure_to_delete')"
            :ok-text="$t('yes')"
            :cancel-text="$t('no')"
            @confirm="deleteRecord(row)"
          >
            <a href="#" :disabled="row._deleting">
              <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
              <i class="fa fa-trash text-red" v-else></i>
            </a>
          </a-popconfirm>
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
      <!-- form-action -->
      <a-modal
        v-if="showForm"
        v-model="showForm"
        :title="$t('supplier.pageTitle')"
        centered
        width="650px"
        @cancel="clearEdit"
        :zIndex="1020"
        :footer="null"
      >
        <from-action @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'
import expandRow from './table-expand.vue'

export default {
  name: 'departmentIndex',
  components: {
    FromAction
  },
  data() {
    return {
      loading: false,
      showForm: false
    }
  },
  computed: {
    ...mapState('supplier/profile', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.supplier.profile.search
      },
      set(newValue) {
        this.$store.commit('supplier/profile/SET_SEARCH', newValue)
        this.$store.commit('supplier/profile/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          type: 'expand',
          render: (h, params) => {
            return h(expandRow, {
              props: {
                row: params.row
              }
            })
          },
          width: 40
        },
        {
          title: this.$t('supplier.code'),
          key: 'vendor_code',
          width: 110
        },
        {
          title: this.$t('supplier.name'),
          key: 'vendor_name',
          width: 200
        },
        {
          title: this.$t('supplier.nameKh'),
          key: 'vendor_name_kh',
          width: 200
        },
        {
          title: this.$t('supplier.vendorIdTin'),
          slot: 'vendorIdTin',
          width: 150
        },
        {
          title: this.$t('supplier.vendorType'),
          slot: 'vendorType',
          width: 180
        },
        {
          title: this.$t('supplier.contactPerson'),
          key: 'contact_person',
          width: 150
        },
        {
          title: this.$t('supplier.contact'),
          slot: 'contactNumber',
          width: 140
        },
        {
          title: this.$t('supplier.email'),
          key: 'contact_email',
          width: 200
        },
        {
          title: this.$t('supplier.creditDay'),
          align: 'center',
          key: 'credit_day',
          width: 110
        },
        {
          title: this.$t('supplier.creditAmount'),
          align: 'center',
          key: 'credit_amount',
          width: 140
        },
        {
          title: this.$t('supplier.isLocal'),
          align: 'center',
          slot: 'isLocal',
          width: 100
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 94
        }
      ]
    }
  },
  methods: {
    vendorTypeLabel(value) {
      const types = {
        '1': 'បុគ្គលជាប់អាករ',
        '2': 'បុគ្គលមិនជាប់អាករ',
        '3': 'ក្រុមហ៊ុនក្រៅប្រទេស'
      }
      return types[String(value)] || ''
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('supplier/profile/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.showForm = true
    },
    edit(record) {
      this.$store.commit('supplier/profile/SET_EDIT_DATA', record)
      this.showForm = true
    },
    clearEdit() {
      this.$store.commit('supplier/profile/SET_EDIT_DATA', {})
      this.showForm = false
      this.fetchData()
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('supplier/profile/destroy', record.vendor_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('supplier/profile/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  }
}
</script>
